import { push } from '@lagunovsky/redux-react-router'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { H_PIEZO_EXPORT_OVERVIEW } from '../../../account/constants/AccessRulesConstants'
import Card from '../../../components/card/Card'
import Button from '../../../components/forms/Button'
import Checkbox from '../../../components/forms/Checkbox'
import Input from '../../../components/forms/Input'
import useTitle from 'utils/customHook/useTitle'
import JobAction from '../../../import/actions/JobAction'
import { nbPerPageLabel } from '../../../referencial/constants/ReferencialConstants'
import { getDate } from '../../../utils/DateUtil'
import { onChangeDate } from '../../../utils/FormUtils'
import { componentHasHabilitations } from '../../../utils/HabilitationUtil'
import { normalizeString } from '../../../utils/StringUtil'
import useActions from 'utils/customHook/useActions'
import { CardTable } from 'components/datatable/NewTable'
import { Box, Grid } from '@mui/material'
import { darkBlue, PASTEL_COLOR } from 'utils/constants/ColorTheme'
import ProgressCard from 'components/card/ProgressCard'

const ExporOverviewList = ({}) => {
    const {
        qesoutHistory,
    } = useSelector(store => ({
        qesoutHistory: store.JobReducer.qesoutHistory,
    }), shallowEqual)

    const dispatch = useDispatch()

    const [dataLoaded, setDataLoaded] = useState(false)
    const [startDate, setStartDate] = useState(moment().subtract(30, 'days').valueOf())
    const [endDate, setEndDate] = useState(moment().valueOf())
    const [data, setData] = useState([])
    const [searchValue, setSearchValue] = useState('')
    const [checkGreen, setCheckGreen] = useState(true)
    const [checkYellow, setCheckYellow] = useState(true)
    const [checkRed, setCheckRed] = useState(true)

    const filterColor = (q) => {
        if (q.colorLine === '#A5D6A7') return checkGreen
        if (q.colorLine === '#FFF176') return checkYellow
        if (q.colorLine === '#EF9A9A') return checkRed
        return true
    }

    const getColor = (q) => {
        if (q.cancelDate) return '#EF9A9A'
        if (q.integrationDate) return '#A5D6A7'
        if (q.sendDate) return '#FFF176'
        return 'white'
    }

    const fetchHistory = () => dispatch(JobAction.fetchExportHistory(startDate, endDate)).then(() => {
        const newData = qesoutHistory.map(q => {
            const obj = {
                code: { value: q.code },
                numNum: { value: q.num },
                startDate: { value: getDate(q.startDate), format: 'dd/MM/yyyy', cellType: 'date' },
                endDate: { value: getDate(q.endDate), format: 'dd/MM/yyyy', cellType: 'date' },
                integration: { value: getDate(q.integrationDate), format: 'dd/MM/yyyy', cellType: 'date' },
                sent: { value: getDate(q.sendDate), format: 'dd/MM/yyyy', cellType: 'date' },
                cancelTime: { value: getDate(q.cancelDate), format: 'dd/MM/yyyy', cellType: 'date' },
                fileLabel: { value: q.fileName },
                colorLine: getColor(q),
            }
            return { ...obj, hash: normalizeString([obj.code.value, obj.numNum.value, obj.startDate.value, obj.endDate.value, obj.sent.value, obj.integration.value, obj.cancelTime.value, obj.fileLabel.value].join('   ')) }
        })
        setDataLoaded(true)
        setData(newData.filter(d => d.hash.includes(normalizeString(searchValue)) && filterColor(d)))
    })

    useEffect(() => {
        if (!componentHasHabilitations(H_PIEZO_EXPORT_OVERVIEW)) { // A modifier quand react-router sera à jour
            dispatch(push('/unauthorized'))
            return
        }
        fetchHistory()
    }, [])

    useTitle(() => [{
        title: i18n.piezometry,
        href: 'piezometry',
    }, {
        title: i18n.exportOverview,
        href: 'piezometry/exportOverview',
    }], [])

    useActions(() => ({
        export: () => {
            const exportData = data
            if (exportData.length) {
                exportData[0].headers = ['code', 'numNum', 'fileLabel', 'startDate', 'endDate', 'sent', 'integration', 'cancelTime']
            }
            return {
                data: exportData,
                exportType: 'xlsx',
                titleFile: i18n.exportOverview,
            }
        },
    }), [data])

    if (!dataLoaded) {
        return <ProgressCard />
    }

    return (
        <>
            <Card>
                <Grid container spacing={1} sx={{ padding: 1, alignItems: 'center' }}>
                    <Grid size={2}>
                        <Input
                            title={i18n.startDate}
                            value={getDate(startDate)}
                            onChange={v => onChangeDate(v, v2 => setStartDate(v2), { max: endDate })}
                        />
                    </Grid>
                    <Grid size={2}>
                        <Input
                            title={i18n.endDate}
                            value={getDate(endDate)}
                            onChange={v => onChangeDate(v, v2 => setEndDate(v2), { min: startDate })}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Input
                            title={i18n.search}
                            value={searchValue}
                            onChange={v => setSearchValue(v)}
                        />
                    </Grid>
                    <Grid size={1}>
                        <Checkbox
                            checked={checkGreen}
                            label={
                                <Box
                                    sx={{
                                        backgroundColor: PASTEL_COLOR.green,
                                        width: '25px',
                                        height: '20px',
                                        borderRadius: '10px',
                                    }}
                                />
                            }
                            onChange={v => setCheckGreen(v)}
                        />
                    </Grid>
                    <Grid size={1}>
                        <Checkbox
                            checked={checkYellow}
                            label={
                                <Box
                                    sx={{
                                        backgroundColor: PASTEL_COLOR.yellow,
                                        width: '25px',
                                        height: '20px',
                                        borderRadius: '10px',
                                    }}
                                />
                            }
                            onChange={v => setCheckYellow(v)}
                        />
                    </Grid>
                    <Grid size={1}>
                        <Checkbox
                            checked={checkRed}
                            label={
                                <Box
                                    sx={{
                                        backgroundColor: PASTEL_COLOR.red,
                                        width: '25px',
                                        height: '20px',
                                        borderRadius: '10px',
                                    }}
                                />
                            }
                            onChange={v => setCheckRed(v)}
                        />
                    </Grid>
                    <Grid size={2}>
                        <Button
                            title={i18n.visualize}
                            onClick={() => {
                                setDataLoaded(false)
                                fetchHistory()
                            }}
                        />
                    </Grid>
                </Grid>
            </Card>
            <CardTable
                id='qesoutExport'
                title={i18n.exportOverview}
                color={darkBlue}
                rows={data}
                headers={['code', 'numNum', 'fileLabel', 'startDate', 'endDate', 'sent', 'integration', 'cancelTime']}
                rowsPerPageOptions={nbPerPageLabel}
            />
        </>
    )
}

ExporOverviewList.propTypes = {
}

export default ExporOverviewList
